import { useEffect, useRef, useState } from "react";
import { assetUrl } from "../data/assets";
import { haptic } from "../platform/telegram";

export type MediaAspect = "feed" | "learn" | "clip" | "sent";

/**
 * Media is first-class in the card. Only *capture* is constrained on this
 * platform — display never was.
 *
 * Aspect is fixed per surface so the well reserves its layout before anything
 * loads: 4:5 in the feed, 16:9 for Learn (lecture content is landscape-native),
 * 9:16 for clips and stories, 3:4 for a photo sent to FroshBot. A failed load
 * keeps the well for the same reason a loading one does — the feed must never
 * reflow mid-scroll.
 */
export function MediaWell({
  url,
  type,
  aspect = "feed",
  durationLabel,
}: {
  url?: string;
  type?: string;
  aspect?: MediaAspect;
  durationLabel?: string;
}) {
  const resolved = assetUrl(url);
  const [status, setStatus] = useState<"loading" | "ready" | "failed">("loading");
  const [attempt, setAttempt] = useState(0);
  const [muted, setMuted] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  const isVideo = type === "video";

  /*
   * Scroll performance is the real webview constraint. The <video> element only
   * mounts once at least half the well is on screen, and unmounts again when it
   * leaves — a feed of simultaneously decoding videos is what actually janks.
   */
  useEffect(() => {
    if (!isVideo) return;
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.intersectionRatio >= 0.5),
      { threshold: [0, 0.5, 1] },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isVideo]);

  if (!resolved) return null;

  return (
    <div ref={containerRef} className={`media-well media-well--${aspect}`}>
      {status === "loading" ? <div className="media-well__shimmer" /> : null}

      {isVideo ? (
        visible ? (
          <video
            key={attempt}
            src={resolved}
            poster={undefined}
            // iOS webviews refuse to autoplay with sound, and refuse inline
            // playback without playsInline. Both are required, not preferences.
            autoPlay
            muted={muted}
            playsInline
            loop
            onLoadedData={() => setStatus("ready")}
            onError={() => setStatus("failed")}
          />
        ) : null
      ) : (
        <img
          key={attempt}
          src={resolved}
          alt=""
          loading="lazy"
          onLoad={() => setStatus("ready")}
          onError={() => setStatus("failed")}
        />
      )}

      {status === "failed" ? (
        <button
          type="button"
          className="media-well__failed"
          onClick={() => {
            setStatus("loading");
            setAttempt((n) => n + 1);
          }}
        >
          <i className="fa-solid fa-arrow-rotate-right" aria-hidden="true" />
          Tap to retry
        </button>
      ) : null}

      {isVideo && durationLabel ? (
        <span className="media-well__duration">{durationLabel}</span>
      ) : null}

      {isVideo && status === "ready" ? (
        <button
          type="button"
          className="media-well__sound"
          onClick={(e) => {
            e.stopPropagation();
            haptic.tap();
            setMuted((m) => !m);
          }}
        >
          <i
            className={muted ? "fa-solid fa-volume-xmark" : "fa-solid fa-volume-high"}
            aria-hidden="true"
          />
          {muted ? "tap for sound" : "sound on"}
        </button>
      ) : null}
    </div>
  );
}
